import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const NoCreditsModal = ({ onClose, planName }) => {
  const [show, setShow] = useState(false);
  const navigate = useNavigate();
  const isUpgradePlan = !planName || planName === 'free' || planName === 'starter';

  useEffect(() => {
    requestAnimationFrame(() => setShow(true));
  }, []);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleAction = () => {
    onClose();
    navigate(isUpgradePlan ? '/plans' : '/topups');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className={`nc-modal relative bg-white dark:bg-gray-900/95 rounded-2xl border border-gray-200/80 dark:border-gray-700/50 shadow-2xl shadow-cyan-500/10 dark:shadow-cyan-500/5 w-full max-w-sm mx-4 overflow-hidden transition-all duration-500 ease-out ${show ? '' : 'opacity-0 scale-95'}`}>
        {/* Top gradient bar */}
        <div className="h-1.5 bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500" />

        <div className="px-8 pt-8 pb-4 text-center">
          {/* Icon */}
          <div className="relative mx-auto w-16 h-16 mb-5">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 opacity-15 dark:opacity-20 blur-xl" />
            <div className="relative w-16 h-16 rounded-2xl p-[2px] bg-gradient-to-br from-blue-400 via-cyan-400 to-purple-400 shadow-lg shadow-cyan-500/20">
              <div className="w-full h-full bg-white dark:bg-gray-900 rounded-[14px] flex items-center justify-center">
                <svg className="w-8 h-8 text-cyan-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
                </svg>
              </div>
            </div>
          </div>

          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1.5">
            You're out of credits 😞
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
            Get more credits to continue running cloud extractions
          </p>

          {/* Divider */}
          <div className="h-px bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent my-5" />

          {/* Value props */}
          <div className="space-y-3 text-left">
            {[
              'Cloud browser runs on autopilot',
              'Scheduled & recurring extractions',
              'Unlock all PRO features in the Browser Extension',
            ].map((label, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500/10 via-cyan-500/10 to-purple-500/10 dark:from-blue-500/15 dark:via-cyan-500/15 dark:to-purple-500/15 border border-cyan-400/15 flex items-center justify-center">
                  <svg className="w-3.5 h-3.5 text-cyan-500 dark:text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                </div>
                <span className="text-sm text-gray-600 dark:text-gray-300">{label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="px-8 pb-6 pt-2 space-y-2">
          <button
            onClick={handleAction}
            className="w-full py-3 rounded-xl font-bold text-sm bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 text-white hover:from-blue-600 hover:via-cyan-600 hover:to-purple-600 transition-all duration-200 shadow-md shadow-cyan-500/20 hover:shadow-lg hover:shadow-cyan-500/30 hover:scale-[1.02] transform"
          >
            {isUpgradePlan ? 'Upgrade Plan' : 'Buy Credits'}
          </button>
          <button
            onClick={onClose}
            className="w-full py-2.5 text-sm text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors font-medium"
          >
            Maybe later
          </button>
        </div>
      </div>

      <style>{`
        .nc-modal {
          animation: nc-shake-in 0.5s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
        }
        @keyframes nc-shake-in {
          0% { opacity: 0; transform: scale(0.95); }
          30% { opacity: 1; transform: scale(1); }
          40% { transform: translateX(-6px); }
          50% { transform: translateX(5px); }
          60% { transform: translateX(-4px); }
          70% { transform: translateX(3px); }
          80% { transform: translateX(-1px); }
          100% { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

export default NoCreditsModal;
